import React, { useState, useEffect, useContext, useRef } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import NoData from '../../../components/NoData';
import { PrintReport } from '../../../components/PrintReport';
import moment from 'moment';
import ReactToPrint from 'react-to-print';

import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';
import { getAbonadoNoAbonadoAsync } from '../../../api/Report';

const Abonado_NoAbonado = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);

	const [userList, setUserList] = useState([]);

	const [gestorName, setGestorName] = useState('');
	const [gestorList, setGestorList] = useState([]);

	const [selectedGestores, setSelectedGestores] = useState([]);

	const compRef = useRef();
	const { selectedSucursal, selectedUser, abonado, fechaDesde, fechaHasta, moneda, tipoCredito } =
		useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatDate = (d) => moment(d).format('L');

	const formatMoney = (row, v) =>
		new Intl.NumberFormat(row.isDolar ? 'en-US' : 'es-NI', {
			style: 'currency',
			currency: row.isDolar ? 'USD' : 'NIO'
		}).format(v);

	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') return setIsUnautorized(true);
		simpleMessage(result.error, 'error');
	};

	const getGestores = async (sucursal) => {
		const r =
			sucursal === 't'
				? await getGestoresBySucursalAccessAsync(token)
				: await getGestoresAsync(token, sucursal);
		if (!r.statusResponse) return handleError(r);
		setGestorList(r.data.result);
		return r.data.result;
	};

	const getSucursals = async () => {
		const r = await getSucursalByUserAsync(token);
		if (!r.statusResponse) return handleError(r);

		const results = r.data.result;
		const filtered =
			selectedSucursal === 't'
				? results
				: results.filter((x) => x.id.toString() === selectedSucursal);

		setSucursalList(filtered);

		await getGestores(selectedSucursal);

		let gestores = await getGestores(selectedSucursal);

		const cadenaDeNumeros = selectedUser;
		const arrayDeStrings = cadenaDeNumeros.split(',');
		const listaDeNumeros = arrayDeStrings.map((str) => Number(str));

		setSelectedGestores(listaDeNumeros);
		if (listaDeNumeros.length === 1) {
			let name = gestores.filter(
				(item) => item.id.toString() === listaDeNumeros[0].toString()
			);
			setGestorName(name[0].fullName);
		} else {
			let usuarios = '';
			listaDeNumeros.map((id) => {
				let name = gestores.filter((item) => item.id.toString() === id.toString());
				usuarios += name[0].fullName + ', ';
			});

			usuarios = usuarios.substring(0, usuarios.length - 2);
			setGestorName(usuarios);
		}

		return filtered;
	};

	const fetchData = async () => {
		const cadenaDeNumeros = selectedUser;
		const arrayDeStrings = cadenaDeNumeros.split(',');
		const listaDeNumeros = arrayDeStrings.map((str) => Number(str));

		setSelectedGestores(listaDeNumeros);

		const payload = {
			desde: fechaDesde.toString(),
			hasta: fechaHasta.toString(),
			gestorId: listaDeNumeros,
			sucursalId: selectedSucursal === 't' ? 0 : selectedSucursal,
			moneda,
			tipoCredito: tipoCredito === 't' ? 0 : tipoCredito,
			abonado: abonado === 't' ? 0 : abonado
		};

		const result = await getAbonadoNoAbonadoAsync(token, payload);
		if (!result.statusResponse) return handleError(result);

		setData(result.data.result);
		setIsLoading(false);
	};

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			await getSucursals();
			await fetchData();
		})();
	}, []);

	const exportExcel = () => {
		const exp = data.map((i) => ({
			credito: i.id,
			cliente: i.fullName,
			direccion: i.address,
			telefono: i.phone,
			fechaEntrega: formatDate(i.fechaEntrega),
			fechaVencimiento: formatDate(i.fechaVencimiento),
			MontoEntregado: i.montoEntregado,
			interesCorriente: i.intCorriente + '%',
			abonado: i.isAbonado,
			tipo: i.isNominal ? 'EMPEÑO' : 'CREDITO',
			saldoCuota: i.saldoCuota,
			saldoTotal: i.saldoTotal,
			sucursal: i.sucursal,
			moneda: i.isDolar ? 'Dólares' : 'Córdobas'
		}));

		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exp), 'Cobrados_NoCobrados');
		XLSX.writeFile(wb, 'Cobrados_NoCobrados.xlsx');
	};

	const totalAbonados = data.filter((x) => x.isAbonado === 'ABONADO').length;
	const totalNoAbonados = data.filter((x) => x.isAbonado === 'NO ABONADO').length;
	const saldoCuotaTotal = data.reduce((acc, x) => acc + x.saldoCuota, 0);
	const saldoTotal = data.reduce((acc, x) => acc + x.saldoTotal, 0);

	const CGestorTable = ({ data, headerFontSize, dataFontSize, isForPrint = false }) => {
		const columns = [
			{ key: 'id', label: '#', align: 'center', getValue: (r) => r.id },
			{ key: 'fullName', label: 'Cliente', align: 'left', getValue: (r) => r.fullName },
			{ key: 'address', label: 'Dirección', align: 'left', getValue: (r) => r.address },
			{ key: 'phone', label: 'Tel', align: 'center', getValue: (r) => r.phone },
			{
				key: 'fechaEntrega',
				label: 'F.Ent',
				align: 'center',
				getValue: (r) => formatDate(r.fechaEntrega)
			},
			{
				key: 'fechaVencimiento',
				label: 'F.Venc',
				align: 'center',
				getValue: (r) => formatDate(r.fechaVencimiento)
			},
			{
				key: 'montoEntregado',
				label: 'Entregado',
				align: 'center',
				getValue: (r) => formatMoney(r, r.montoEntregado)
			},
			{
				key: 'ic',
				label: 'Interes C.',
				align: 'center',
				getValue: (r) => r.intCorriente + '%'
			},
			{ key: 'isAbonado', label: 'Abonado', align: 'center', getValue: (r) => r.isAbonado },
			{
				key: 'tipo',
				label: 'Tipo',
				align: 'center',
				getValue: (r) => (r.isNominal ? 'EMPEÑO' : 'CREDITO')
			},
			{
				key: 'saldoCuota',
				label: 'SCuota',
				align: 'center',
				getValue: (r) => formatMoney(r, r.saldoCuota)
			},
			{
				key: 'saldoTotal',
				label: 'STotal',
				align: 'center',
				getValue: (r) => formatMoney(r, r.saldoTotal)
			}
		];

		let abonadosGestor = 0;
		let noAbonadosGestor = 0;
		let saldoCuotaGestor = 0;
		let saldoTotalGestor = 0;

		return (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className={
											isForPrint
												? 'text-dark fw-bold'
												: 'text-primary fw-bold'
										}
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>

						<TableBody>
							{data.map((r) => {
								// console.log('🚀 ~ r:', r);

								if (r.isAbonado === 'ABONADO') {
									abonadosGestor += 1;
								} else {
									noAbonadosGestor += 1;
								}

								saldoCuotaGestor += r.saldoCuota;
								saldoTotalGestor += r.saldoTotal;

								return (
									<TableRow key={r.id}>
										{columns.map((col) => (
											<TableCell
												key={col.key}
												align={col.align}
												style={{ fontSize: dataFontSize }}
												className={isForPrint ? 'text-dark' : ''}
											>
												{col.getValue(r)}
											</TableCell>
										))}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<hr />
				<Stack direction="row" flex="row" justifyContent="space-around">
					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Abonados
						</Typography>
						<Typography style={{ fontSize: 11 }}>{abonadosGestor}</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							No Abonados
						</Typography>
						<Typography style={{ fontSize: 11 }}>{noAbonadosGestor}</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo Cuota
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(saldoCuotaGestor)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo Total
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(saldoTotalGestor)}
						</Typography>
					</Stack>
				</Stack>
				<hr />
			</>
		);
	};

	const paramText = (
		<>
			<Typography fontSize={11}>
				{`Sucursal: ${
					selectedSucursal === 't' ? 'Todas' : sucursalList[0]?.description || ''
				}`}
			</Typography>

			<Typography fontSize={11}>{`Desde: ${formatDate(fechaDesde)} - Hasta: ${formatDate(
				fechaHasta
			)}`}</Typography>

			<Typography fontSize={11}>
				{`Moneda: ${parseInt(moneda) === 1 ? 'Cordoba' : 'Dolar'}
         - Tipo: ${
				tipoCredito === 't'
					? 'Todos'
					: tipoCredito === '1'
						? 'EMPEÑO'
						: tipoCredito === '2'
							? 'CREDITO'
							: 'CARTERA'
			}
         - ${
				abonado === 't'
					? 'ABONADOS Y NO ABONADOS'
					: abonado === '1'
						? 'ABONADOS'
						: 'NO ABONADOS'
			}`}
			</Typography>
		</>
	);

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }  
  @page { size: landscape;  margin: 5mm; }
`;

	return (
		<Dialog fullScreen open={true}>
			<AppBar sx={{ position: 'relative' }}>
				<Toolbar>
					<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
					<Typography
						sx={{ ml: 2, flex: 1, textAlign: 'center' }}
						variant="h5"
						component="div"
					>
						{`${title} `}
					</Typography>
					<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
				</Toolbar>
			</AppBar>

			<Stack display="flex" justifyContent="center">
				<Stack direction="row" justifyContent="space-between" px={5} mt={1} mb={1}>
					<IconButton onClick={exportExcel}>
						<FontAwesomeIcon
							icon={faCloudArrowDown}
							style={{ fontSize: 40, color: '#00a152' }}
						/>
					</IconButton>

					<Stack textAlign="center">
						<Typography color="#2196f3" fontWeight="bold">
							{slogan}
						</Typography>
						<Typography color="#2196f3" fontWeight="bold" variant="h6">
							Reporte de Creditos Abonados y No Abonados
						</Typography>
					</Stack>

					<ReactToPrint
						trigger={() => {
							return (
								<IconButton
									variant="outlined"
									// style={{ position: "fixed", right: 20, top: 75 }}
								>
									<PrintRoundedIcon
										style={{ fontSize: 40, color: '#2979ff', width: 40 }}
									/>
								</IconButton>
							);
						}}
						content={() => compRef.current}
						pageStyle={pageStyle}
					/>
					{/* <IconButton>
						<PrintRoundedIcon style={{ fontSize: 40, color: '#2979ff' }} />
					</IconButton> */}
				</Stack>

				<Container maxWidth={false}>
					<Stack direction="row" justifyContent="space-between">
						<Stack>{paramText}</Stack>
						<Stack>
							<Typography fontSize={11}>Usuario: {user}</Typography>
							<Typography fontSize={11}>{rol}</Typography>
							<Typography fontSize={11}>{`Fecha y Hora: ${moment().format(
								'DD/MM/YYYY hh:mm A'
							)}`}</Typography>
						</Stack>
					</Stack>
				</Container>
			</Stack>

			<Container maxWidth={false}>
				<Divider sx={{ my: 1 }} />

				{isEmpty(data) ? (
					<NoData />
				) : (
					sucursalList.map((suc) => {
						const porSucursal = data.filter((c) => c.sucursal === suc.description);

						return (
							<Stack key={suc.description}>
								<Typography fontSize={11} color="#2196f3">
									Sucursal: {suc.description}
								</Typography>

								{gestorList.map((g) => {
									const porGestor = porSucursal.filter((c) => c.gestor === g.id);
									if (isEmpty(porGestor)) return null;

									return (
										<Stack key={g.id} p={1}>
											<Typography fontSize={11} color="#2196f3">
												Gestor: {g.fullName}
											</Typography>

											<Divider />

											<CGestorTable
												data={porGestor}
												headerFontSize={9}
												dataFontSize={8}
											/>
										</Stack>
									);
								})}
							</Stack>
						);
					})
				)}

				<Divider />
				<Stack direction="row" justifyContent="space-around" fontSize={11}>
					<Stack textAlign="center">
						<b style={{ color: '#03a9f4' }}>Abonados</b>
						{totalAbonados}
					</Stack>

					<Stack textAlign="center">
						<b style={{ color: '#03a9f4' }}>No Abonados</b>
						{totalNoAbonados}
					</Stack>

					<Stack textAlign="center">
						<b style={{ color: '#03a9f4' }}>Saldo Cuota</b>
						{formatMoney({ isDolar: parseInt(moneda) === 2 }, saldoCuotaTotal)}
					</Stack>

					<Stack textAlign="center">
						<b style={{ color: '#03a9f4' }}>Saldo Total</b>
						{formatMoney({ isDolar: parseInt(moneda) === 2 }, saldoTotal)}
					</Stack>
				</Stack>
				<Divider />
			</Container>

			{/* PRINT */}
			<div style={{ display: 'none' }}>
				<PrintReport
					ref={compRef}
					titulo="Reporte de Creditos Abonados y No Abonados"
					fecha={formatDate(moment())}
				>
					<Container maxWidth={false}>
						<Stack direction="row" justifyContent="space-between">
							<Stack>{paramText}</Stack>
							<Stack>
								<Typography fontSize={11}>Usuario: {user}</Typography>
								<Typography fontSize={11}>{rol}</Typography>
								<Typography fontSize={11}>{`Fecha y Hora: ${moment().format(
									'DD/MM/YYYY hh:mm A'
								)}`}</Typography>
							</Stack>
						</Stack>
					</Container>

					<Container maxWidth={false}>
						<Divider sx={{ my: 1 }} />

						{isEmpty(data) ? (
							<NoData />
						) : (
							sucursalList.map((suc) => {
								const porSucursal = data.filter(
									(c) => c.sucursal === suc.description
								);

								return (
									<Stack key={suc.description}>
										<Typography fontSize={11} color="#2196f3">
											Sucursal: {suc.description}
										</Typography>

										{gestorList.map((g) => {
											const porGestor = porSucursal.filter(
												(c) => c.gestor === g.id
											);
											if (isEmpty(porGestor)) return null;

											return (
												<Stack key={g.id} p={1}>
													<Typography fontSize={11} color="#2196f3">
														Gestor: {g.fullName}
													</Typography>

													<Divider />

													<CGestorTable
														data={porGestor}
														headerFontSize={9}
														dataFontSize={8}
														isForPrint={true}
													/>

													<Divider />

													<Stack
														direction="row"
														justifyContent="space-around"
														fontSize={11}
													>
														<Stack textAlign="center">
															<b style={{ color: '#03a9f4' }}>
																Abonados
															</b>
															{totalAbonados}
														</Stack>

														<Stack textAlign="center">
															<b style={{ color: '#03a9f4' }}>
																No Abonados
															</b>
															{totalNoAbonados}
														</Stack>

														<Stack textAlign="center">
															<b style={{ color: '#03a9f4' }}>
																Saldo Cuota
															</b>
															{formatMoney(
																{ isDolar: parseInt(moneda) === 2 },
																saldoCuotaTotal
															)}
														</Stack>

														<Stack textAlign="center">
															<b style={{ color: '#03a9f4' }}>
																Saldo Total
															</b>
															{formatMoney(
																{ isDolar: parseInt(moneda) === 2 },
																saldoTotal
															)}
														</Stack>
													</Stack>
												</Stack>
											);
										})}
									</Stack>
								);
							})
						)}
					</Container>
				</PrintReport>
			</div>
		</Dialog>
	);
};

export default Abonado_NoAbonado;
